import { assert } from "@/utils/tools"
import {
  CONNECT_STATE_CONN_CONNECTED,
  CONNECT_STATE_CONN_FAILED,
  CONNECT_STATE_MAX_RETRY,
  CONNECT_STATE_PEER_ACCEPT_CONN,
  CONNECT_STATE_PEER_CANCEL,
  CONNECT_STATE_PEER_CLOSE,
  CONNECT_STATE_PEER_NODISPLAY,
  CONNECT_STATE_PEER_REJECT_CONN,
  CONNECT_STATE_PWD_VERIFY_FAIL,
  CONNECT_STATE_RECONN,
  ConnStateInfo,
  KeyboardEventData,
  MouseEventData,
  OnRemoteCopy,
  PeerConnConfig,
  Resolution,
  ServerConfig,
  _getLocalDevId,
  _reportSDKError,
  _reportSDKLog,
} from "."
import {
  _CUSTOM_MSG_ID_CAMERA_STATE_CHANGE,
  _CUSTOM_MSG_ID_MIC_STATE_CHANGE,
  _DEFAULT_CONN_CONFIG,
  _MSG_CODE_CALL,
  _MSG_CODE_BLOCK_APP_INSTALL,
  _MSG_CODE_CALL_ACK,
  _MSG_CODE_END_CALL,
  _MSG_CODE_FILE_TRANSFER_CONTROL,
  _MSG_CODE_GET_VIDEO_DPI,
  _MSG_CODE_GET_PEER_DEVICE_INFO,
  _MSG_CODE_GET_PEER_DEVICE_INFO_ACK,
  _MSG_CODE_KEYBOARD,
  _MSG_CODE_MOUSE,
  _MSG_CODE_REPLY_RESULT,
  _MSG_CODE_SET_CUSTOM_DATA,
  _MSG_CODE_SET_VIDEO_DPI,
  _MSG_CODE_SET_VIDEO_RESOLUTION,
  _MSG_CODE_SET_WALLPAPER,
  _MSG_CODE_SHOW_REMOTE_CURSOR,
  _MSG_CODE_SEND_JSON_DATA,
  _CUSTOM_MSG_ID_FIRST_FRAME_RENDER,
  _CUSTOM_MSG_ID_CLIENT_CONFIG,
} from "./constants"
import { OnCursorChange, _CursorEventProcessor } from "./cursor_event_processor"
import { FileTransferManager } from "./file_transfer_task"
import { geelevel } from "./proto_message"
import { _SegDataChannel } from "./seg_data_channel"
import { _ConnStatsProcessor } from "./statistic"
import {
  DEFAULT_RELAY_PORT,
  DEFAULT_RELAY_PWD,
  DEFAULT_RELAY_USER,
  ERR_ICE,
  ERR_UNRECV_REMOTE_ICE,
  ERR_UNRECV_LOCAL_ICE,
  ERR_UNRECV_ANSWER,
  ERR_RENDER_TIMEOUT,
  ERR_KICK_OUT,
  ERR_POWER_OFF,
  ERR_AGENT_REBOOT,
  ERR_NETWORK_NOT_RECOVER,
} from "@/models/constants"
import { v4 as uuidv4 } from "uuid"

const DATA_CHANNEL_MOUSE = "channel-mouse"
const DATA_CHANNEL_MOUSE_CURSOR = "channel-mouse-cursor"
const DATA_CHANNEL_KEYBOARD = "channel-keyboard"
const DATA_CHANNEL_CUSTOM_MESSAGE = "custom-message"
const DATA_CHANNEL_HEART_BEAT = "data-channel-heart-beat"
const PREFERRED_VIDEO_CODECS = ["video/H265", "video/HEVC", "video/H264"]

// interface SdpVideoCodec {
//   payloadType: string
//   mimeType: string
//   fmtp?: string
// }

// function getSdpVideoCodecs(sdp?: string): SdpVideoCodec[] {
//   if (sdp === undefined) return []

//   const lines = sdp.split(/\r?\n/)
//   const videoStartIndex = lines.findIndex((line) => line.startsWith("m=video"))
//   if (videoStartIndex < 0) return []

//   const videoEndIndex = lines.findIndex(
//     (line, index) => index > videoStartIndex && line.startsWith("m=")
//   )
//   const videoLines = lines.slice(
//     videoStartIndex,
//     videoEndIndex < 0 ? undefined : videoEndIndex
//   )
//   const payloadTypes = videoLines[0]?.split(/\s+/).slice(3) ?? []
//   const rtpMaps = new Map<string, string>()
//   const fmtps = new Map<string, string>()

//   videoLines.forEach((line) => {
//     const rtpMap = line.match(/^a=rtpmap:(\d+)\s+([^\s/]+\/\d+)/i)
//     if (rtpMap !== null) rtpMaps.set(rtpMap[1], rtpMap[2])

//     const fmtp = line.match(/^a=fmtp:(\d+)\s+(.+)$/i)
//     if (fmtp !== null) fmtps.set(fmtp[1], fmtp[2])
//   })

//   return payloadTypes.flatMap((payloadType) => {
//     const mimeType = rtpMaps.get(payloadType)
//     if (mimeType === undefined) return []
//     return [{ payloadType, mimeType, fmtp: fmtps.get(payloadType) }]
//   })
// }

// function findCodecById(
//   stats: RTCStatsReport,
//   codecId: string | undefined
// ): string {
//   if (codecId === undefined) return "unknown"
//   const codec = stats.get(codecId)
//   return codec?.type === "codec" && typeof codec.mimeType === "string"
//     ? codec.mimeType
//     : "unknown"
// }

// function toStringOrUndefined(value: unknown): string | undefined {
//   return typeof value === "string" ? value : undefined
// }

export interface MediaStreams {
  video?: MediaStream
  audio?: MediaStream
}

// WebRTC 连接过程中的远端 SDP 的回调
export interface _OnRemotePeerCallback {
  // WebRTC 连接状态变化回调
  onPeerConnect: (user: string, state: number) => void

  // 收到远端的 answer
  onAnswer: (data: RTCSessionDescriptionInit) => void

  // 收到远端的 ICE candidate
  onIceCandidate: (data: RTCIceCandidateInit) => void

  // 信令连接过期
  onSignalingExpired: () => void
}

export enum _WebRTCCloseAction {
  FINISH = 0,
  USER_CLOSE = 1,
  REMOTE_CLOSE = 2,
}

interface DisplaySetting {
  label: string
  is_primary: boolean
  width?: number
  height?: number
  settings: Resolution[]
}

interface System {
  buildNumber: number
}

export interface DeviceInfo {
  display: DisplaySetting[]
  system: System
  keyboard_version?: string
  using_hardware_type?: string
}

interface MediaConfig {
  mediaType: number
  videoType: number
  audioType: number
  recordDeviceIndex: number
  playerDeviceIndex: number
  echoCancellation: boolean
}

interface ICECandidateStats {
  ip: string
  networkType: string
  protocol: string
  candidateType: string
}

interface KCDeskWebRTCProps {
  callbacks: WebRTCCallback
  config: PeerConnConfig
}

interface CustomMessageReply {
  result: number
  data?: Uint8Array
}

interface WebRTCCallback {
  // 与远端连接状态变化
  onConnect: (state: number, errCode?: number) => void
  // 获取设备支持的分辨率
  onGetDisplayInfo: (resolutions: Resolution[]) => void
  // 获取 Agent 主显示器当前分辨率
  onGetAgentResolution: (resolution: Resolution) => void
  // 通过信令服务器发送消息到远端
  sendMsgBySignaling: (data: any) => void
  // 等待信令恢复，重连前必须确保 Offer 能发送出去。
  waitSignalingConnected: (timeoutMs: number) => Promise<boolean>
  // 获取Agent版本信息
  onGetAgentVersion: (buildNumber: number) => void
  // 获取键盘方案版本信息
  onGetKeyboardVersion: (keyboardVersion?: string) => void
  // 是否为游戏类型云电脑
  onGetIsGameCloudComputer: (isGameCloudComputer: boolean) => void
  // Agent 通知客户端开启/关闭麦克风
  onMicStateChange: (isOpen: boolean) => void
  // Agent 通知客户端开启/关闭摄像头
  onCameraStateChange: (isOpen: boolean) => void
  // Agent 通知客户端：云电脑禁止安装安全/虚拟化软件（对齐 android KCSdkCallback.onBlockAppInstallation）
  onBlockAppInstallation?: () => void
}
interface Msg {
  type: RTCSdpType
  sdp: string | undefined
  media_type: number
  video_type: number
  audio_type: number
  record_device_index: number
  player_device_index: number
  echo_cancellation: boolean
  forceRelay: boolean
  relay?: Array<{
    addr: string
    user: string
    pwd: string
  }>
  client_type: string
}

interface Msg {
  type: RTCSdpType
  sdp: string | undefined
  media_type: number
  video_type: number
  audio_type: number
  record_device_index: number
  player_device_index: number
  echo_cancellation: boolean
  forceRelay: boolean
  relay?: Array<{
    addr: string
    user: string
    pwd: string
  }>
  client_type: string
}

// 连接超时时间 (单位: ms)
const CONN_TIMEOUT = 20000
const FIRST_FRAME_TIMEOUT = 10000
// connection disconnected 后等待自愈的超时，超时则重连。
// web/windows 的 ICE 常停在 disconnected 不升级为 failed（浏览器行为），
// 故需要 connection 层兜底，不能像 android 那样只靠 ICE failed。
const CONNECTION_RECOVERY_TIMEOUT = 15000
const RECONNECT_DELAY = 3000
const SIGNALING_RECOVERY_TIMEOUT = 15000
// 最大尝试重连次数
const MAX_RETRY_TIMES = 2

export class _KCDeskWebRTC {
  private pc!: RTCPeerConnection
  // WebRTC 连接配置
  private readonly peerConfig: PeerConnConfig
  private readonly rtcConfig: RTCConfiguration

  private readonly callbacks: WebRTCCallback
  private readonly dataChannels = new Map<string, _SegDataChannel>()
  private readonly replyWaiters = new Map<
    number,
    Array<(reply: CustomMessageReply | null) => void>
  >()

  // 远端音视频流
  private mediaStreams: MediaStreams = {}
  // 本地音频输入流
  private audioInputStream: MediaStream | null = null
  private audioInputSender: RTCRtpSender | null = null
  // 与实际 sender 分离的麦克风期望状态，用于 PeerConnection 重建后的恢复。
  private isAudioInputEnabled = false

  // 本地视频输入流
  private videoInputStream: MediaStream | null = null
  private videoInputSender: RTCRtpSender | null = null
  // 与实际 sender 分离的摄像头期望状态，用于 PeerConnection 重建后的恢复。
  private isVideoInputEnabled = false

  private mediaConfig: MediaConfig = {
    // 2 代表连接上远端且允许开启音频
    mediaType: 2,
    // 0 代表展示第一个显示器的画面
    videoType: 0,
    // 1 代表开启音频
    audioType: 1,
    recordDeviceIndex: -1,
    playerDeviceIndex: -1,
    echoCancellation: false,
  }

  // 用来处理视频数据统计
  readonly connStatistic: _ConnStatsProcessor
  // 管理文件传输
  fileTransferManager!: FileTransferManager
  // 处理鼠标 Cursor 样式事件
  readonly cursorProcessor: _CursorEventProcessor
  // 粘贴板复制时的回调
  private onRemoteCopy: OnRemoteCopy | null = null

  // relay 服务器内网ip
  private relayInnerIp: string | null = null
  // relay 服务器外网ip
  private relayPubIp: string | null = null

  // Agent build number
  private agentBuildNum: number | null = null

  // 是否远端发起关闭
  private isCloseByRemoteDesk = false

  get isConnected(): boolean {
    return this.pc.connectionState === "connected"
  }

  // 重连次数
  private reconnTimes = 0

  // 连接超时定时任务
  private retryTimeoutTimer: number | null = null
  // 同一时刻只允许一个 PeerConnection 重建流程，避免 disconnected、failed、
  // 超时监控等多个事件重复触发重连提示和重建。
  private reconnectPromise: Promise<void> | null = null
  private reconnectNoticeShown = false
  private reconnectGeneration = 0
  // ICE 短暂断开后，浏览器可能已关闭 SCTP DataChannel；连接恢复时需检查并重建。
  private needDataChannelRecovery = false

  private isCleared = false
  private startConnTime: number | null = null

  // 连接唯一标识
  readonly connId = uuidv4()

  // 是否获取到客户端的 ICE candidate
  private isGetClientICECandidate = false
  // 是否获取到远端的 ICE candidate
  private isGetRemoteICECandidate = false
  private isGetRemoteAnswer = false
  private timeoutReported = false
  private firstFrameRenderedReported = false
  private firstFrameTimer: number | null = null

  private isMediaRenegotiation = false
  private mediaRenegotiationWaiter: {
    resolve: () => void
    reject: (error: Error) => void
    timer: number
  } | null = null
  // 音频和视频开关可能由 Agent 同时下发；媒体 SDP 协商必须串行，
  // 否则后一次操作会中断前一次的 waiter。

  private mediaOperationQueue: Promise<void> = Promise.resolve()

  private enqueueMediaOperation<T>(operation: () => Promise<T>): Promise<T> {
    const run = this.mediaOperationQueue.then(operation, operation)
    this.mediaOperationQueue = run.then(
      () => undefined,
      () => undefined
    )
    return run
  }

  // 网络恢复软重协商（对齐 android _listenNetworkStatus 的 restartIce 路径）：
  // 网络恢复后不销毁 PC，等信令恢复后 restartIce + sendOffer 重新协商，
  // 上报 signal_ice_renegotiation。比全量 tryReconnect 更轻量。
  private readonly onNetworkOnline = (): void => {
    void this.recoverAfterNetworkOnline()
  }

  private readonly onNetworkOffline = (): void => {
    this.rejectMediaRenegotiation(
      new Error("WebRTC network became unavailable during media renegotiation")
    )
  }

  constructor({ config, callbacks }: KCDeskWebRTCProps) {
    this.peerConfig = config
    this.callbacks = callbacks
    this.peerConfig = config

    // 创建PeerConnection
    this.rtcConfig = this.createRTCConfig(config)
    console.log("WebRTC peer connection config: ", this.rtcConfig)
    this.cursorProcessor = new _CursorEventProcessor()
    this.connStatistic = new _ConnStatsProcessor()
  }

  /**
   * WebRTC 连接到远端前的准备工作，包括：
   * 1. 创建所有的 data channel
   * 2. 监听 ICE candidate
   * 3. 监听远端视频流
   */
  start() {
    console.log("WebRTC start connect")
    console.log("开始连接时间：", Date.now())
    this.startConnTime = Date.now()

    this.isCleared = false
    // 全量重连（tryReconnect→clear→start）创建了全新 PC，createDataChannels() 已建好
    // 全部 DataChannel，connected 后 finishConnectionSetup 不应再 recoverDataChannels
    // 触发第二次 sendOffer。重置此标志，避免旧 disconnected 阶段设置的 true 残留导致
    // 双倍 SDP 协商。软恢复路径不走 start()，不受影响。
    this.needDataChannelRecovery = false
    this.pc = new RTCPeerConnection(this.rtcConfig)
    this.clearRetryTimeout()
    this.retryTimeoutTimer = window.setTimeout(() => {
      if (!this.timeoutReported) {
        this.timeoutReported = true
        const errCode = !this.isGetClientICECandidate
          ? ERR_UNRECV_LOCAL_ICE
          : !this.isGetRemoteAnswer
            ? ERR_UNRECV_ANSWER
            : !this.isGetRemoteICECandidate
              ? ERR_UNRECV_REMOTE_ICE
              : null
        if (errCode !== null) {
          this.reportWebRTCError(errCode, {
            msg: "WebRTC连接10秒内未收到必要信令",
          })
        }
      }
      console.error("WebRTC connection timeout")
      this.retryTimeoutTimer = null
      // 连接超时，尝试重连
      this.tryReconnect()
    }, CONN_TIMEOUT)
    window.addEventListener("online", this.onNetworkOnline)
    window.addEventListener("offline", this.onNetworkOffline)
    this.fileTransferManager = new FileTransferManager(
      this.pc,
      this.sendCustomMessage.bind(this)
    )
    // 上报开始连接云桌面
    this.reportWebRTCLog("signal_start")

    // 调整编码器顺序
    this.changeCodecOrder()
    this.changeCodecOrderAudio()

    // 减少延迟
    this.reduceDelay()

    // 创建所有的 data channel
    this.createDataChannels()

    // 监听 ICE candidate
    this.pc.addEventListener("icecandidate", this.onICECandidate.bind(this))
    this.pc.addEventListener(
      "negotiationneeded",
      this.onNegotiationNeeded.bind(this)
    )
    this.pc.addEventListener(
      "icegatheringstatechange",
      this.onICEGathering.bind(this)
    )
    this.pc.addEventListener(
      "icecandidateerror",
      this.onICECandidateError.bind(this)
    )
    this.pc.addEventListener(
      "iceconnectionstatechange",
      this.onICEStateChange.bind(this)
    )

    this.pc.addEventListener("datachannel", this.onDataChannel.bind(this))

    // 监听远端视频流
    this.pc.addEventListener("track", this.onTrack.bind(this))

    // 监听连接状态
    this.pc.addEventListener(
      "connectionstatechange",
      this.onConnectionChange.bind(this)
    )

    // 监听信令状态
    this.pc.addEventListener(
      "signalingstatechange",
      this.onSignalingStateChange.bind(this)
    )

    // 添加连接状态持续监控
    this.startConnectionMonitoring()

    // 发送Offer
    this.sendOffer()
  }

  private tryReconnect(): void {
    if (
      this.reconnectPromise !== null ||
      this.isCloseByRemoteDesk ||
      this.isConnected
    ) {
      return
    }

    // 上报 WebRTC 开始重连（与 Android 的 signal_try_reconnect 对齐）
    this.reportWebRTCLog("signal_try_reconnect")

    // 重连诊断：检查上次连接是否收到必要信令，对齐 android _tryReconnect
    // 的三段独立上报（各自检查、各自上报，非互斥）。此时三个标志位仍是上次
    // 连接的值，performReconnect 内 clear() 才重置。
    if (!this.isGetRemoteAnswer) {
      this.reportWebRTCError(ERR_UNRECV_ANSWER, { msg: "未收到 Answer" })
    }
    if (!this.isGetClientICECandidate) {
      this.reportWebRTCError(ERR_UNRECV_LOCAL_ICE, {
        msg: "未收到本地 ICE candidate",
        client_ip: this.relayPubIp,
        agent_ip: this.relayInnerIp,
      })
    }
    if (!this.isGetRemoteICECandidate) {
      this.reportWebRTCError(ERR_UNRECV_REMOTE_ICE, {
        msg: "未收到远端 ICE Candidate",
        client_ip: this.relayPubIp,
        agent_ip: this.relayInnerIp,
      })
    }

    const generation = ++this.reconnectGeneration
    this.reconnectPromise = this.performReconnect(generation).finally(() => {
      if (this.reconnectGeneration === generation) {
        this.reconnectPromise = null
      }
    })
  }

  private async performReconnect(generation: number): Promise<void> {
    const lastAttemptDiagnostics = {
      connection: this.pc.connectionState,
      ice: this.pc.iceConnectionState,
      signaling: this.pc.signalingState,
    }
    if (this.reconnTimes >= MAX_RETRY_TIMES) {
      this.clear()
      console.error("WebRTC reconnect limit reached: ", lastAttemptDiagnostics)
      // 本地网络断开并长时间未恢复时，补充细粒度诊断上报（对齐 android
      // ErrCode.networkNotRecover = -10226）。不改变 MAX_RETRY→ERR_CONN_TIMEOUT
      // 的退出语义，仅为诊断区分。
      if (!navigator.onLine) {
        this.reportWebRTCError(ERR_NETWORK_NOT_RECOVER, {
          msg: "本地网络断开长时间未恢复",
        })
      }
      this.callOnConnect(CONNECT_STATE_MAX_RETRY)
      this.connStatistic.finish()
      return
    }

    this.notifyReconnecting()
    // 让 UI 在关闭旧 PeerConnection 前冻结最后一帧，避免浏览器 video
    // 在等待新远端流期间直接变黑。
    this.clear()
    this.reconnTimes++
    console.log("WebRTC reconnect, retry time: ", this.reconnTimes)
    this.reportWebRTCError(ERR_ICE, { msg: "尝试重连" })

    await this.wait(RECONNECT_DELAY)
    if (this.reconnectGeneration !== generation || this.isCloseByRemoteDesk) {
      return
    }

    const signalingConnected = await this.waitForSignalingRecovery()
    if (this.reconnectGeneration !== generation || this.isCloseByRemoteDesk) {
      return
    }
    if (!signalingConnected) {
      console.warn("WebRTC signaling is unavailable, retry later")
      this.scheduleReconnect(RECONNECT_DELAY)
      return
    }

    this.start()
  }

  private notifyReconnecting(): void {
    if (this.reconnectNoticeShown) {
      return
    }
    this.reconnectNoticeShown = true
    this.callOnConnect(CONNECT_STATE_RECONN)
  }

  private scheduleReconnect(delayMs: number): void {
    if (this.retryTimeoutTimer !== null || this.isCloseByRemoteDesk) {
      return
    }
    this.retryTimeoutTimer = window.setTimeout(() => {
      this.retryTimeoutTimer = null
      this.tryReconnect()
    }, delayMs)
  }

  private cancelPendingReconnect(): void {
    this.reconnectGeneration++
    this.clearRetryTimeout()
  }

  private wait(delayMs: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, delayMs))
  }

  private async waitForSignalingRecovery(): Promise<boolean> {
    try {
      return await this.callbacks.waitSignalingConnected(
        SIGNALING_RECOVERY_TIMEOUT
      )
    } catch (error) {
      console.warn("WebRTC cannot wait for signaling recovery: ", error)
      return false
    }
  }

  // 调整编码器顺序：优先 H265，H264 兜底，保留 RTX/FEC 等配套 codec
  private changeCodecOrder() {
    const videoCapabilities =
      // RTCRtpSender.getCapabilities?.("video") ??
      RTCRtpReceiver.getCapabilities?.("video")
    console.log("SDP 编码器能力:", videoCapabilities)
    const supportedCodecs = videoCapabilities?.codecs ?? []
    // const h265Codecs = supportedCodecs.filter((codec) =>
    //   ["video/H265", "video/HEVC"].some(
    //     (mimeType) => mimeType.toLowerCase() === codec.mimeType.toLowerCase()
    //   )
    // )
    // const h264Codecs = supportedCodecs.filter(
    //   (codec) => codec.mimeType.toLowerCase() === "video/H264"
    // )
    // console.info("WebRTC client video codec capability:", {
    //   userAgent: navigator.userAgent,
    //   h265Supported: h265Codecs.length > 0,
    //   h264Supported: h264Codecs.length > 0,
    //   h265Codecs,
    //   h264Codecs,
    // })

    const videoTransceiver = this.pc.addTransceiver("video", {
      direction: "recvonly",
    })

    if (supportedCodecs.length > 0) {
      try {
        const codecs = [...supportedCodecs].sort((a, b) => {
          const indexA = PREFERRED_VIDEO_CODECS.findIndex(
            (mimeType) => mimeType.toLowerCase() === a.mimeType.toLowerCase()
          )
          const indexB = PREFERRED_VIDEO_CODECS.findIndex(
            (mimeType) => mimeType.toLowerCase() === b.mimeType.toLowerCase()
          )
          const orderA = indexA >= 0 ? indexA : Number.MAX_SAFE_INTEGER
          const orderB = indexB >= 0 ? indexB : Number.MAX_SAFE_INTEGER

          return orderA - orderB
        })

        console.log("SDP 编码器排序:", codecs)
        videoTransceiver.setCodecPreferences(codecs)
      } catch (e) {
        console.error("WebRTC setCodecPreferences error: ", e)
      }
    } else {
      this.reportWebRTCError(ERR_ICE, {
        msg: "SDP 无可用视频编解码器，期望: H265/H264",
      })
    }
  }

  private changeCodecOrderAudio() {
    this.pc.addTransceiver("audio", { direction: "recvonly" })
  }

  private isWindowsChrome(): boolean {
    const userAgent = navigator.userAgent
    return (
      userAgent.includes("Windows") &&
      userAgent.includes("Chrome") &&
      !userAgent.includes("Edg/") &&
      !userAgent.includes("OPR/")
    )
  }

  private normalizeOfferForCompatibility(
    offer: RTCSessionDescriptionInit
  ): RTCSessionDescriptionInit {
    const sdp = offer.sdp
    if (
      sdp === undefined ||
      !this.isWindowsChrome() ||
      !/a=rtpmap:\d+\s+H265\/90000/i.test(sdp)
    ) {
      return offer
    }

    const normalizedSdp = this.simplifyWindowsChromeH265OfferSdp(sdp)
    if (normalizedSdp === sdp) {
      return offer
    }

    // console.log(
    //   "WebRTC normalized Windows Chrome H265 offer SDP:",
    //   normalizedSdp
    // )
    return {
      ...offer,
      sdp: normalizedSdp,
    }
  }

  private simplifyWindowsChromeH265OfferSdp(sdp: string): string {
    const lines = sdp.split("\r\n")
    const videoSectionStart = lines.findIndex((line) =>
      line.startsWith("m=video ")
    )
    if (videoSectionStart === -1) {
      return sdp
    }

    let videoSectionEnd = lines.findIndex(
      (line, index) => index > videoSectionStart && line.startsWith("m=")
    )
    if (videoSectionEnd === -1) {
      videoSectionEnd = lines.length
    }

    const videoPayloadTypes = new Set(
      lines[videoSectionStart].split(" ").slice(3)
    )
    const h265PayloadTypes = new Set(
      lines
        .slice(videoSectionStart, videoSectionEnd)
        .map(
          (line) => line.match(/^a=rtpmap:(\d+)\s+H265\/90000$/i)?.[1] ?? null
        )
        .filter((payloadType): payloadType is string => payloadType !== null)
        .filter((payloadType) => videoPayloadTypes.has(payloadType))
    )
    if (h265PayloadTypes.size === 0) {
      return sdp
    }

    const normalizedLines = lines.filter((line, index) => {
      if (index <= videoSectionStart || index >= videoSectionEnd) {
        return true
      }

      const fmtpLineMatch = line.match(/^a=fmtp:(\d+)\b/i)
      if (fmtpLineMatch !== null && h265PayloadTypes.has(fmtpLineMatch[1])) {
        return false
      }

      return true
    })

    return normalizedLines.join("\r\n")
  }

  // 减少延迟的配置
  private reduceDelay() {
    const receivers = this.pc.getReceivers()
    if (receivers.length > 0 && receivers) {
      // Chrome 124 以上可用
      const receiver: any = receivers[0]
      if (receiver.jitterBufferTarget !== undefined) {
        receiver.jitterBufferTarget = 30
      }
    }
  }

  private createRTCConfig(config: PeerConnConfig): RTCConfiguration {
    const iceServers: RTCIceServer[] = []
    const isSafari =
      navigator.userAgent.includes("Safari") &&
      !navigator.userAgent.includes("Chrome")

    if (config.relaySever != null) {
      // 使用传入的 relay 服务配置
      iceServers.push(config.relaySever)
    }

    if (iceServers.length === 0) {
      // 使用默认 relay 服务配置
      iceServers.push(
        ..._DEFAULT_CONN_CONFIG.relay.map(
          (server: ServerConfig): RTCIceServer => ({
            urls: "turn:" + server.addr,
            username: server.user,
            credential: server.pwd,
          })
        )
      )
    }

    // Safari TURN 服务器兼容性处理
    if (isSafari) {
      // 备份原始 ICE 服务器配置
      const originalIceServers = [...iceServers]

      // 清空并重新配置 ICE 服务器以确保 Safari 兼容性
      iceServers.length = 0

      // 检查 TURN 服务器配置格式并修复
      originalIceServers.forEach((server, index) => {
        if (typeof server.urls === "string" && server.urls.includes("turn:")) {
          // Safari 需要多种 URL 格式
          const baseUrl = server.urls.replace(/\?.*$/, "") // 移除现有参数

          // 为 Safari 添加多种传输协议的 TURN 服务器
          const safariTurnServer = {
            urls: [
              `${baseUrl}?transport=udp`,
              `${baseUrl}?transport=tcp`,
              baseUrl, // 保持原始格式作为备用
            ],
            username: server.username,
            credential: server.credential,
          }

          iceServers.push(safariTurnServer)
          // console.log(`wlp Modified TURN Server for Safari:`, safariTurnServer)
        } else if (Array.isArray(server.urls)) {
          // 处理 URLs 数组
          const expandedUrls: string[] = []
          server.urls.forEach((url) => {
            if (url.includes("turn:")) {
              const baseUrl = url.replace(/\?.*$/, "")
              expandedUrls.push(`${baseUrl}?transport=udp`)
              expandedUrls.push(`${baseUrl}?transport=tcp`)
              expandedUrls.push(url)
            } else {
              expandedUrls.push(url)
            }
          })

          const safariTurnServer = {
            urls: expandedUrls,
            username: server.username,
            credential: server.credential,
          }
          iceServers.push(safariTurnServer)
          // console.log(
          //   `wlp Modified array TURN Server for Safari:`,
          //   safariTurnServer
          // )
        }
      })
    }
    let iceTransportPolicy: RTCIceTransportPolicy = config.forceRelay
      ? "relay"
      : "all"
    this.relayPubIp = config.preferIp

    // Safari 特殊处理：如果强制使用 relay 但可能存在兼容性问题，使用 'all' 策略
    if (isSafari && config.forceRelay) {
      iceTransportPolicy = "all" // 允许 Safari 尝试所有连接类型
    }
    if (config.remotePeerInnerIp !== null) {
      this.relayInnerIp = config.remotePeerInnerIp
    }

    const rtcConfig: RTCConfiguration = {
      iceServers,
      iceTransportPolicy,
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
    }

    if (isSafari) {
      // 确保 Safari 有足够的时间收集候选者
      rtcConfig.iceCandidatePoolSize = 10 // 增加候选者池大小
      rtcConfig.iceTransportPolicy = iceTransportPolicy
    }

    return rtcConfig
  }

  // 发送 offer 给远端
  private async sendOffer(peerConnection = this.pc) {
    // 创建 offer
    let offer: RTCSessionDescriptionInit
    try {
      offer = await peerConnection.createOffer({
        iceRestart: true,
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      })
      offer = this.normalizeOfferForCompatibility(offer)
      await peerConnection.setLocalDescription(offer)
    } catch (e) {
      console.error("WebRTC failed to create offer: ", e)
      return
    }

    if (this.pc !== peerConnection || this.isCleared) {
      return
    }

    // console.info("WebRTC client offer video codecs:", {
    //   codecs: getSdpVideoCodecs(peerConnection.localDescription?.sdp),
    // })

    const {
      mediaType,
      videoType,
      audioType,
      recordDeviceIndex,
      playerDeviceIndex,
      echoCancellation,
    } = this.mediaConfig

    const msg: Msg = {
      type: offer.type,
      sdp: offer.sdp,
      media_type: mediaType,
      video_type: videoType,
      audio_type: audioType,
      record_device_index: recordDeviceIndex,
      player_device_index: playerDeviceIndex,
      echo_cancellation: echoCancellation,
      forceRelay: this.peerConfig.forceRelay,
      client_type: "web",
    }
    if (this.peerConfig.remotePeerInnerIp) {
      msg.relay = [
        {
          addr: `${this.peerConfig.remotePeerInnerIp}:${DEFAULT_RELAY_PORT}`,
          user: DEFAULT_RELAY_USER,
          pwd: DEFAULT_RELAY_PWD,
        },
      ]
    }

    // console.log("WebRTC offer SDP:", offer.sdp)
    console.log(
      "offer SDP has flexfec-03:",
      offer.sdp?.toLowerCase().includes("flexfec-03"),
      "ulpfec:",
      offer.sdp?.toLowerCase().includes("ulpfec"),
      "rtx:",
      offer.sdp?.toLowerCase().includes("rtx"),
      "red:",
      offer.sdp?.toLowerCase().includes("red"),
      "nack:",
      offer.sdp?.toLowerCase().includes("nack"),
      "fec:",
      offer.sdp?.toLowerCase().includes("fec")
    )
    console.log("WebRTC send offer")
    console.log("WebRTC Signaling send offer msg: ", msg)

    this.callbacks.sendMsgBySignaling(msg)

    // 上报本地设备媒体信息
    this.reportWebRTCLog("signal_client_sdp")
  }

  private waitForMediaRenegotiation(timeoutMs = 15000): Promise<void> {
    if (this.mediaRenegotiationWaiter !== null) {
      window.clearTimeout(this.mediaRenegotiationWaiter.timer)
      this.mediaRenegotiationWaiter.reject(
        new Error("WebRTC media renegotiation was interrupted")
      )
      this.mediaRenegotiationWaiter = null
    }

    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.mediaRenegotiationWaiter = null
        reject(new Error("WebRTC media renegotiation timed out"))
      }, timeoutMs)

      this.mediaRenegotiationWaiter = {
        resolve,
        reject,
        timer,
      }
    })
  }

  private resolveMediaRenegotiation(): void {
    this.isMediaRenegotiation = false
    if (this.mediaRenegotiationWaiter === null) {
      return
    }

    window.clearTimeout(this.mediaRenegotiationWaiter.timer)
    this.mediaRenegotiationWaiter.resolve()
    this.mediaRenegotiationWaiter = null
  }

  private rejectMediaRenegotiation(error: Error): void {
    this.isMediaRenegotiation = false
    if (this.mediaRenegotiationWaiter === null) {
      return
    }

    window.clearTimeout(this.mediaRenegotiationWaiter.timer)
    this.mediaRenegotiationWaiter.reject(error)
    this.mediaRenegotiationWaiter = null
  }

  /**
   * 处理 ICE candidate 信息
   * @param event ICE candidate 事件
   */
  private onICECandidate(event: RTCPeerConnectionIceEvent): void {
    // close() 之后旧 PeerConnection 仍可能异步派发最后一个 candidate；
    // 不能把它发送到已经关闭或新建连接的信令通道。
    if (
      this.isCleared ||
      event.target !== this.pc ||
      event.candidate === null
    ) {
      return
    }

    const candidate = event.candidate
    console.log(
      `WebRTC get local ICE candidate: ${candidate.address}:${candidate.port} ${candidate.protocol} ${candidate.type}`
    )
    let candidateStr: string = ""

    if (candidate.address === this.peerConfig.preferIp) {
      const candidateList = candidate.candidate.split(" ")
      candidateList[3] = "4294967295"

      candidateStr = candidateList.join(" ")
    } else {
      candidateStr = candidate.candidate
    }

    // 发送本地 ICE candidate 到远端
    const msg = {
      type: "candidate",
      sdpMid: candidate.sdpMid,
      sdpMLineIndex: candidate.sdpMLineIndex,
      candidate: candidateStr,
    }

    this.callbacks.sendMsgBySignaling(msg)

    if (this.isGetClientICECandidate) {
      return
    }
    this.isGetClientICECandidate = true

    // 上报本地 ICE 连接成功
    this.reportWebRTCLog("signal_client_candidate", {
      candidate: candidateStr,
    })
  }

  private onNegotiationNeeded(event: Event) {
    console.log("WebRTC onNegotiationNeeded")
  }

  private onICEGathering(event: any) {
    console.log("WebRTC ICE gathering state: ", event.target.iceGatheringState)
  }

  private onICECandidateError(event: any) {
    const isSafari =
      navigator.userAgent.includes("Safari") &&
      !navigator.userAgent.includes("Chrome")

    // Safari 特定的 ICE 错误处理
    if (isSafari) {
      // TURN 服务器连接错误
      if (event.errorCode === 701 || event.errorText?.includes("TURN")) {
        // console.error("wlp Safari TURN server connection failed:", event)
        this.reportWebRTCError(ERR_ICE, {
          msg: "Safari TURN服务器连接失败",
          errorCode: event.errorCode,
          errorText: event.errorText,
          url: event.url,
          suggestion: "请检查TURN服务器配置和网络连接",
        })
      }
      // 认证错误
      else if (event.errorCode === 401) {
        // console.error("wlp Safari TURN authentication failed:", event)
        this.reportWebRTCError(ERR_ICE, {
          msg: "Safari TURN认证失败",
          errorCode: event.errorCode,
          url: event.url,
          suggestion: "请检查TURN服务器用户名和密码",
        })
      }
      // 网络连接错误
      else if (event.errorCode >= 700 && event.errorCode < 800) {
        // console.error("wlp Safari network error:", event)
        this.reportWebRTCError(ERR_ICE, {
          msg: "Safari网络连接错误",
          errorCode: event.errorCode,
          suggestion: "可能存在防火墙或网络限制",
        })
      }
      // 地址不可达错误
      else if (event.errorCode === 600) {
        // console.error("wlp Safari address unreachable:", event)
        this.reportWebRTCError(ERR_ICE, {
          msg: "Safari无法访问指定地址",
          errorCode: event.errorCode,
          address: event.address,
          port: event.port,
        })
      }
    }
  }

  private onDataChannel(event: RTCDataChannelEvent) {
    // 初始化接收文件任务的 DataChannel
    this.fileTransferManager.initRecvDataChannel(event)
  }

  private onICEStateChange(event: Event) {
    const peerConnection = event.target as RTCPeerConnection
    if (peerConnection !== this.pc || this.isCleared) {
      return
    }
    const iceState = peerConnection.iceConnectionState
    console.log("WebRTC iceconnectionstatechange: ", iceState)
    switch (iceState) {
      case "connected": {
        // 上报 ICE 连接成功
        this.reportWebRTCLog("signal_client_ice_success")
        // 上报 ICE 网络连接信息
        this.reportICEStats()
        break
      }
      case "closed": {
        // 上报 ICE 关闭
        this.reportWebRTCLog("signal_client_ice_closed")
        break
      }
      case "disconnected":
        this.needDataChannelRecovery = true
        // 上报 ICE 断开
        this.reportWebRTCLog("signal_client_ice_disconnect")
        break
      case "failed": {
        // 上报 ICE 错误
        this.reportWebRTCLog("signal_client_ice_err")
        // ICE 失败触发重连，对齐 android _onIceConnectionStateChange 的 failed
        // 分支调 _tryReconnect()。android 重连只靠 ICE failed 触发（connection
        // 层 disconnected/failed 为空 break），故此处必须调 tryReconnect，
        // 否则 ICE failed 后无重连、画面卡死。
        this.tryReconnect()
        break
      }
    }
  }

  private onTrack(event: RTCTrackEvent) {
    if (event.streams.length === 0) {
      console.warn("No stream found")
      return
    }
    const stream = event.streams[0]
    const isVideoStream = stream.getVideoTracks().length > 0
    if (isVideoStream) {
      console.log("WebRTC get remote video stream")
      this.mediaStreams.video = stream
    }
    const isAudioStream = stream.getAudioTracks().length > 0
    if (isAudioStream) {
      console.log("WebRTC get remote audio stream")
      this.mediaStreams.audio = stream
    }
  }

  async getAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    this.isGetRemoteAnswer = true
    // 上报获取到远端设备媒体信息
    this.reportWebRTCLog("signal_agent_sdp")
    // console.log("WebRTC answer SDP:", answer.sdp)
    console.log(
      "answer SDP has flexfec-03:",
      answer.sdp?.toLowerCase().includes("flexfec-03"),
      "ulpfec:",
      answer.sdp?.toLowerCase().includes("ulpfec"),
      "rtx:",
      answer.sdp?.toLowerCase().includes("rtx"),
      "red:",
      answer.sdp?.toLowerCase().includes("red"),
      "nack:",
      answer.sdp?.toLowerCase().includes("nack"),
      "fec:",
      answer.sdp?.toLowerCase().includes("fec")
    )
    // console.info("WebRTC agent answer video codecs:", {
    //   codecs: getSdpVideoCodecs(answer.sdp),
    //   note: "Answer SDP 表示 Agent 本次协商接受的编解码器；实际发送编码以运行时 RTP 统计为准。",
    // })

    // Answer 只对应当前本地 offer。手动断开后可能收到旧信令的迟到 answer，
    // 此时 PeerConnection 已处于 stable，直接忽略以免破坏本次连接。
    if (this.pc.signalingState !== "have-local-offer") {
      // console.warn(
      //   "WebRTC ignore stale answer in signaling state:",
      //   this.pc.signalingState
      // )
      return
    }

    try {
      await this.pc.setRemoteDescription(new RTCSessionDescription(answer))
      console.log("WebRTC get answer")
      this.resolveMediaRenegotiation()
    } catch (e) {
      console.error("WebRTC failed to process answer: ", e)
      this.rejectMediaRenegotiation(
        e instanceof Error
          ? e
          : new Error("WebRTC failed to process media renegotiation answer")
      )
    }
  }

  async onRemoteICECandidate(candidate: RTCIceCandidateInit): Promise<void> {
    console.log(`WebRTC get remote ICE candidate: `, candidate.candidate)
    try {
      // 添加远端 ICE candidate
      await this.pc.addIceCandidate(candidate)
    } catch (e) {
      console.error("WebRTC failed to add remote ICE candidate: ", e)
    }

    if (this.isGetRemoteICECandidate) {
      return
    }
    this.isGetRemoteICECandidate = true
    // 上报获取到远端设备 ICE candidate
    this.reportWebRTCLog("signal_agent_candidate")
  }

  // 处理Webrtc连接状态变化
  private onConnectionChange(event: Event): void {
    const peerConnection = event.target as RTCPeerConnection
    if (peerConnection !== this.pc || this.isCleared) {
      return
    }
    const currentState = peerConnection.connectionState
    const isSafari =
      navigator.userAgent.includes("Safari") &&
      !navigator.userAgent.includes("Chrome")

    switch (currentState) {
      case "connected":
        console.log("WebRTC connected")
        // void this.logInboundVideoCodec(peerConnection)
        this.clearRetryTimeout()
        this.firstFrameRenderedReported = false
        if (this.firstFrameTimer !== null)
          window.clearTimeout(this.firstFrameTimer)
        this.firstFrameTimer = window.setTimeout(() => {
          this.firstFrameTimer = null
          if (!this.firstFrameRenderedReported && !this.isCleared) {
            this.reportWebRTCError(ERR_RENDER_TIMEOUT, {
              screen: this.mediaConfig.videoType === 0 ? "main" : "sub",
              msg: "WebRTC连接成功10秒后仍未渲染首帧",
            })
          }
        }, FIRST_FRAME_TIMEOUT)
        // 连接成功，清零重连次数
        this.reconnTimes = 0
        this.reconnectNoticeShown = false
        this.isMediaRenegotiation = false
        void this.finishConnectionSetup(this.pc)
        console.log(
          "连接成功时间：",
          Date.now(),
          ";开始连接-连接成功的差值：",
          Date.now() - this.startConnTime!,
          "ms"
        )
        console.log(
          "Test*** onConected, transceiver: ",
          this.pc.getTransceivers()
        )
        break
      case "disconnected":
        // connection 断开：给 ICE 自愈时间，15s 未恢复则重连。
        // web/windows 的 ICE 常停在 disconnected 不升级 failed（浏览器行为），
        // 故需要此兜底，不能像 android 那样空 break。
        console.error(`WebRTC connection state: ${this.pc.connectionState}`)
        if (!this.isCloseByRemoteDesk && !this.isMediaRenegotiation) {
          this.notifyReconnecting()
          this.scheduleReconnect(CONNECTION_RECOVERY_TIMEOUT)
        }
        break
      case "failed":
        // connection failed：立即重连。web/windows 的 ICE 不升级到 failed，
        // connection failed 是断网后的最终信号，故立即触发 tryReconnect。
        console.error(`WebRTC connection state: ${this.pc.connectionState}`)
        if (!this.isCloseByRemoteDesk && !this.isMediaRenegotiation) {
          this.tryReconnect()
        }
        break
      case "closed":
        if (!this.isCleared) {
          // 如果不是主动清理导致的关闭，可能是异常
          this.reportWebRTCError(ERR_ICE, {
            msg: "连接意外关闭",
            browser: isSafari ? "Safari" : "Other",
          })
        }
        break
      default:
        console.log("WebRTC connection state: ", this.pc.connectionState)
    }
  }

  private async finishConnectionSetup(
    peerConnection: RTCPeerConnection
  ): Promise<void> {
    try {
      const needDataChannelRecovery = this.needDataChannelRecovery
      this.needDataChannelRecovery = false
      if (needDataChannelRecovery) {
        await this.enqueueMediaOperation(() =>
          this.recoverDataChannels(peerConnection)
        )
      }
      await this.recoverAudioInput()
    } catch (error) {
      console.error("WebRTC recover audio input error: ", error)
    }
    try {
      await this.recoverVideoInput()
    } catch (error) {
      console.error("WebRTC recover video input error: ", error)
    }

    if (
      this.pc === peerConnection &&
      this.pc.connectionState === "connected" &&
      !this.isCleared
    ) {
      // Android 在每次 Connected 后都会重新发送 Call。软恢复时自定义通道可能
      // 没有触发 open 事件，必须显式发送，Agent 才会恢复 msg_id=12 的状态推送。
      this.sendCall()
      this.setShowRemoteCursor()
      this.getDeviceInfo()
      this.callOnConnect(CONNECT_STATE_CONN_CONNECTED)
    }
  }

  private onSignalingStateChange(event: Event): void {
    const peerConnection = event.target as RTCPeerConnection
    if (peerConnection !== this.pc || this.isCleared) {
      return
    }
    const state = peerConnection.signalingState
    console.log("WebRTC signal state change: ", state)
    if (state === "closed") {
      this.reportWebRTCLog("signal_client_signaling_closed")
      // 无法恢复的异常，比如机器息屏，通知用户断开连接
      this.callOnConnect(CONNECT_STATE_CONN_FAILED)
      this.clear()
    }
  }

  // 设置远端视频流分辨率
  setVideoResolution(width: number, height: number, index: number = 0): void {
    try {
      const msg = geelevel.protobuf.SetResolution.create({
        index,
        width,
        height,
      })
      const buffer = geelevel.protobuf.SetResolution.encode(msg).finish()
      this.sendCustomMessage(_MSG_CODE_SET_VIDEO_RESOLUTION, buffer) // code data
    } catch (e) {
      console.log("WebRTC set video resolution error, ", e)
    }
  }

  // 发送 心跳包
  sendHeartbeatData(heartBeatText: string) {
    if (!this.isConnected) {
      return
    }

    const channel = this.dataChannels.get(DATA_CHANNEL_HEART_BEAT)
    assert(
      channel !== undefined,
      "WebRTC cannot find data channel to send heart beat data"
    )
    try {
      const jsonData = geelevel.protobuf.JsonData.create({
        data: heartBeatText,
      })
      const buffer = geelevel.protobuf.JsonData.encode(jsonData).finish()

      if (channel !== undefined) {
        channel.send(_MSG_CODE_SEND_JSON_DATA, buffer)
      }
    } catch (e) {
      console.log("WebRTC send heart beat data error: ", e)
    }
  }

  sendMouseData(event: MouseEventData): void {
    if (!this.isConnected) {
      return
    }

    const channel = this.dataChannels.get(DATA_CHANNEL_MOUSE)
    assert(
      channel !== undefined,
      "WebRTC cannot find data channel to send mouse data"
    )
    try {
      const mouseMsg = geelevel.protobuf.MouseData.create(event)
      const buffer = geelevel.protobuf.MouseData.encode(mouseMsg).finish()
      if (channel !== undefined) {
        channel.send(_MSG_CODE_MOUSE, buffer)
      }
    } catch (e) {
      console.log("WebRTC send mouse data error: ", e)
    }
  }

  // 发送键盘事件
  sendKeyboardData(event: KeyboardEventData): void {
    if (!this.isConnected) {
      return
    }

    const channel = this.dataChannels.get(DATA_CHANNEL_KEYBOARD)
    assert(
      channel !== undefined,
      "WebRTC cannot find data channel to send keyboard data"
    )

    try {
      const keyboardMsg = geelevel.protobuf.KeyboardData.create({
        event: event.event,
        keyCode: event.code,
        keyboardState: event.state,
      })
      // encode：消息->二进制数据 （序列化）     decode：二进制数据->消息 （反序列化）
      const buffer = geelevel.protobuf.KeyboardData.encode(keyboardMsg).finish()
      if (channel !== undefined) {
        channel.send(_MSG_CODE_KEYBOARD, buffer)
      }
    } catch (e) {
      console.log("WebRTC send keyboard data error: ", e)
    }
  }

  // 发送 清理键盘事件
  sendCleanKeyboardData() {
    const data = { type: "keyboard", op: "clean", msg_id: 3, time: Date.now() }
    try {
      const jsonData = geelevel.protobuf.JsonData.create({
        data: JSON.stringify(data),
      })
      const buffer = geelevel.protobuf.JsonData.encode(jsonData).finish()

      this.sendCustomMessage(_MSG_CODE_SEND_JSON_DATA, buffer)
    } catch (e) {
      console.error("WebRTC send clean keyboard data error: ", e)
    }
  }

  // 透传客户端动态配置给远端 agent（msg_id=21）
  sendClientConfig(config: Record<string, unknown>) {
    const data = { msg_id: _CUSTOM_MSG_ID_CLIENT_CONFIG, ...config }
    console.log(`[${new Date().toLocaleString("zh-CN", { hour12: false })}] [sendClientConfig] payload:`, data)   // 临时
    try {
      const jsonData = geelevel.protobuf.JsonData.create({
        data: JSON.stringify(data),
      })
      const buffer = geelevel.protobuf.JsonData.encode(jsonData).finish()

      this.sendCustomMessage(_MSG_CODE_SEND_JSON_DATA, buffer)
    } catch (e) {
      console.error("WebRTC send client config error: ", e)
    }
  }

  async getStatsData(): Promise<ConnStateInfo | null> {
    const report = await this.pc.getStats()
    return this.connStatistic.getStateInfo(report)
  }

  // private async logInboundVideoCodec(
  //   peerConnection: RTCPeerConnection
  // ): Promise<void> {
  //   await new Promise((resolve) => window.setTimeout(resolve, 2000))
  //   if (this.isCleared || this.pc !== peerConnection) return

  // try {
  // const stats = await peerConnection.getStats()
  // const inboundVideo = [...stats.values()]
  //   .filter(
  //     (report) => report.type === "inbound-rtp" && report.kind === "video"
  //   )
  //   .map((report) => ({
  //     codec: findCodecById(
  //       stats,
  //       toStringOrUndefined(
  //         (report as unknown as Record<string, unknown>).codecId
  //       )
  //     ),
  //     packetsReceived: Number(report.packetsReceived ?? 0),
  //     framesReceived: Number(report.framesReceived ?? 0),
  //     framesDecoded: Number(report.framesDecoded ?? 0),
  //     decoder: report.decoderImplementation ?? "unknown",
  //   }))
  // console.info("WebRTC runtime inbound video codec (Agent actual use):", {
  //   inboundVideo,
  // })
  // } catch (error) {
  // console.warn("WebRTC cannot read runtime inbound video codec:", error)
  // }
  // }

  async sendCustomMessage(code: number, data?: Uint8Array) {
    const channel = this.dataChannels.get(DATA_CHANNEL_CUSTOM_MESSAGE)
    if (channel === undefined) {
      // console.warn(
      //   "WebRTC skip custom message: data channel is not available",
      //   { code, connectionState: this.pc?.connectionState }
      // )
      return
    }

    if (channel.isClose) {
      // console.warn("WebRTC skip custom message: data channel is closed", {
      //   code,
      // })
      return
    }
    if (channel.isConnecting) {
      // 连接断开时 DataChannel 可能永远不会再触发 open；避免首帧/控制消息悬挂。
      await Promise.race([
        channel.waitUntilOpen(),
        new Promise<void>((resolve) => window.setTimeout(resolve, 5000)),
      ])
    }

    if (!channel.isOpen || this.isCleared) {
      // console.warn("WebRTC skip custom message: data channel did not open", {
      //   code,
      //   state: channel._channel.readyState,
      // })
      return
    }

    channel.send(code, data)
  }

  private onCustomMessage(code: number, data?: Uint8Array): void {
    try {
      switch (code) {
        case _MSG_CODE_CALL_ACK: {
          this.onAck(data)
          break
        }
        case _MSG_CODE_END_CALL: {
          this.onEndCall(data)
          break
        }
        case _MSG_CODE_REPLY_RESULT: {
          this.onMsgReply(data)
          break
        }
        case _MSG_CODE_SET_CUSTOM_DATA: {
          // 获取到粘贴板数据
          this.onGetClipboardData(data)
          break
        }
        case _MSG_CODE_GET_PEER_DEVICE_INFO_ACK: {
          this.onGetDeviceInfo(data)
          break
        }
        case _MSG_CODE_SEND_JSON_DATA: {
          this.onCustomJsonMessage(data)
          break
        }
        case _MSG_CODE_BLOCK_APP_INSTALL: {
          // 云电脑端禁止安装安全/虚拟化软件（对齐 android MsgCode.blockAppInstall）
          this.callbacks.onBlockAppInstallation?.()
          break
        }
        default: {
          // 处理文件传输相关的事件
          this.fileTransferManager.processCustomMsg(code, data)
        }
      }
    } catch (e) {
      console.log("WebRTC receive custom message error: ", e)
    }
  }

  private onCustomJsonMessage(data?: Uint8Array): void {
    if (data === undefined) return
    try {
      const jsonMessage = geelevel.protobuf.JsonData.decode(data)
      const rawMessage: unknown = jsonMessage.data
      if (typeof rawMessage !== "string") return
      const message = JSON.parse(rawMessage) as unknown
      if (typeof message !== "object" || message === null) return
      const { msg_id: msgId, op } = message as Record<string, unknown>
      if (msgId === _CUSTOM_MSG_ID_MIC_STATE_CHANGE) {
        if (op === "open") this.callbacks.onMicStateChange(true)
        if (op === "close") this.callbacks.onMicStateChange(false)
      }
      if (msgId === _CUSTOM_MSG_ID_CAMERA_STATE_CHANGE) {
        if (op === "open") this.callbacks.onCameraStateChange(true)
        if (op === "close") this.callbacks.onCameraStateChange(false)
      }
    } catch (error) {
      console.error("WebRTC receive custom JSON message error: ", error)
    }
  }

  // 收到对端的 ack，表示对端已经收到了 call 消息
  private onAck(data?: Uint8Array): void {
    if (data === undefined) {
      console.error("WebRTC receive call ack, but data is null")
      return
    }

    const ack = geelevel.protobuf.CallAck.decode(data)
    console.log("WebRTC receive call ack: ", ack)
    if (ack.action === 0) {
      // 对端接受连接
      this.callOnConnect(CONNECT_STATE_PEER_ACCEPT_CONN)
      if (this.agentBuildNum !== null && this.agentBuildNum < 21) {
        this.setShowRemoteCursor()
      }
    } else {
      if (ack.reason === 0) {
        this.callOnConnect(CONNECT_STATE_PEER_REJECT_CONN)
      } else {
        this.callOnConnect(CONNECT_STATE_PWD_VERIFY_FAIL)
      }
    }
  }

  // 收到对端的 end call 消息
  private onEndCall(data?: Uint8Array): void {
    let reason: number | undefined
    if (data) {
      const endCall = geelevel.protobuf.EndCall.decode(data)
      reason = endCall.reason as number
    }
    console.log("WebRTC receive end call: ", reason)
    this.isCloseByRemoteDesk = true
    this.cancelPendingReconnect()
    if (this.isConnected) {
      // reason 映射对齐 android _onEndCall：6=agent 重启，7=agent 关机，其余=被踢
      let errCode: number
      if (reason === 6) {
        errCode = ERR_AGENT_REBOOT
      } else if (reason === 7) {
        errCode = ERR_POWER_OFF
      } else {
        errCode = ERR_KICK_OUT
      }
      this.callOnConnect(CONNECT_STATE_PEER_CLOSE, errCode)
      // 上报被踢出连接
      this.reportWebRTCLog("signal_agent_get_newlink")
    } else {
      this.callOnConnect(CONNECT_STATE_PEER_CANCEL)
    }
    this.clear()
  }

  // 发送消息的回复
  private onMsgReply(data?: Uint8Array): void {
    if (data === undefined) {
      console.error("WebRTC receive reply result, but data is null")
      return
    }
    const reply = geelevel.protobuf.ReplayResult.decode(data)
    console.log("WebRTC receive reply result: ", reply)
    const replyCode = Number(reply.code)
    const waiters = this.replyWaiters.get(replyCode)
    const waiter = waiters?.shift()
    if (waiters !== undefined && waiters.length === 0) {
      this.replyWaiters.delete(replyCode)
    }
    waiter?.({
      result: Number(reply.result),
      data: reply.data as Uint8Array,
    })
  }

  private callOnConnect(state: number, errCode?: number): void {
    this.callbacks.onConnect(state, errCode)
  }

  // 清理 WebRTC 的连接资源
  clear(): void {
    if (this.isCleared) {
      return
    }

    this.rejectMediaRenegotiation(
      new Error("WebRTC connection was cleared during media renegotiation")
    )
    // 先标记清理状态，忽略 close() 同步或异步派发的旧连接事件。
    this.isCleared = true

    // 停止连接监控
    if (this.connectionMonitorTimer) {
      clearInterval(this.connectionMonitorTimer)
      this.connectionMonitorTimer = null
    }

    // 清理文件传输任务
    this.fileTransferManager.clear()
    // 关闭 data channel
    this.dataChannels.forEach((channel) => {
      channel.close()
    })
    this.dataChannels.clear()

    // 关闭 PeerConnection
    this.pc.close()
    // 上报连接断开
    this.reportWebRTCLog("signal_stop")

    // 清理连接相关资源
    this.cursorProcessor.clear()
    this.mediaStreams = {}
    this.clearRetryTimeout()
    window.removeEventListener("online", this.onNetworkOnline)
    window.removeEventListener("offline", this.onNetworkOffline)

    // 重连会创建新的 PeerConnection。保留本地 MediaStream 供上层恢复，
    // 但旧 sender 属于已关闭的 PeerConnection，不能复用。
    this.audioInputSender = null
    this.videoInputSender = null

    this.isGetClientICECandidate = false
    this.isGetRemoteICECandidate = false
    this.isGetRemoteAnswer = false
    this.timeoutReported = false
    this.firstFrameRenderedReported = false
    if (this.firstFrameTimer !== null) {
      window.clearTimeout(this.firstFrameTimer)
      this.firstFrameTimer = null
    }

    this.agentBuildNum = null
  }

  private clearRetryTimeout() {
    if (this.retryTimeoutTimer !== null) {
      window.clearTimeout(this.retryTimeoutTimer)
      this.retryTimeoutTimer = null
    }
  }

  // 网络恢复后软重协商（对齐 android _listenNetworkStatus 的 restartIce 路径）：
  // 不销毁 PC，等信令恢复后 restartIce + sendOffer 重新协商，上报
  // signal_ice_renegotiation。比全量 tryReconnect 更轻量，优先尝试软恢复。
  private async recoverAfterNetworkOnline(): Promise<void> {
    if (
      this.isCleared ||
      this.isCloseByRemoteDesk ||
      this.isConnected ||
      this.reconnectPromise !== null
    ) {
      return
    }

    const peerConnection = this.pc
    const signalingConnected = await this.waitForSignalingRecovery()
    if (
      !signalingConnected ||
      this.isCleared ||
      this.pc !== peerConnection ||
      this.isCloseByRemoteDesk
    ) {
      return
    }

    // 信令等待期间连接已自愈：无需软恢复。
    if (this.isConnected) {
      return
    }
    // 已有 Offer 在途（初始连接或全量重连刚发出 Offer、signalingState=have-local-offer，
    // 或媒体重协商在途）时跳过软恢复，避免叠加第二次 SDP。对齐 android restartIce 与
    // tryReconnect 互斥的语义。
    if (peerConnection.signalingState === "have-local-offer") {
      return
    }

    // 提交软恢复：取消挂起的全量重连定时器，避免 15s 兜底在 restartIce 协商期间抢先
    // 触发 tryReconnect 产生第二次 SDP。软恢复成功（connection→connected）由
    // onConnectionChange 清定时器；sendOffer 抛错则 catch 内 tryReconnect 兜底；
    // sendOffer 成功但 ICE 未恢复则下方重新排程一轮自愈窗口。
    this.clearRetryTimeout()

    try {
      // 软恢复：一次协商同时完成 ICE restart + DataChannel 重建，避免与 connected 后
      // recoverDataChannels 叠加产生第二次 SDP。对齐 android：DC 重建并入软恢复路径，
      // connected 后不再单独 sendOffer。
      peerConnection.restartIce()
      // 断网恢复后重启 ICE，开始重新协商
      this.reportWebRTCLog("signal_ice_renegotiation")
      // 标记 DC 恢复已由本次软恢复承担，connected 后 finishConnectionSetup 不再
      // recoverDataChannels(sendOffer)，避免第二次 SDP 协商。
      this.needDataChannelRecovery = false
      // 通过媒体操作队列串行执行 DC 重建 + sendOffer，避免与并发的摄像头/麦克风
      // 开关（同样走该队列）竞态触发重复协商。与 finishConnectionSetup 调用方式一致。
      await this.enqueueMediaOperation(() =>
        this.recoverDataChannels(peerConnection)
      )
      // Offer 已发出但连接尚未恢复：重新挂起一轮自愈窗口，超时再全量重连。
      if (
        !this.isCleared &&
        this.pc === peerConnection &&
        !this.isCloseByRemoteDesk &&
        !this.isConnected
      ) {
        this.scheduleReconnect(CONNECTION_RECOVERY_TIMEOUT)
      }
    } catch (error) {
      console.error("WebRTC restart ICE after network recovery failed: ", error)
      this.tryReconnect()
    }
  }

  // 创建 data channel
  private createDataChannels(): void {
    // 创建心跳包 的 data channel
    const heartbeatChannel = new _SegDataChannel(
      this.pc.createDataChannel(DATA_CHANNEL_HEART_BEAT, {
        ordered: true,
      })
    )
    this.dataChannels.set(DATA_CHANNEL_HEART_BEAT, heartbeatChannel)

    // 创建自定义消息的 data channel
    const customMsgChannel = new _SegDataChannel(
      this.pc.createDataChannel(DATA_CHANNEL_CUSTOM_MESSAGE),
      { onOpen: this.sendCall.bind(this) }
    )
    customMsgChannel.setOnMessageListener(this.onCustomMessage.bind(this))
    this.dataChannels.set(DATA_CHANNEL_CUSTOM_MESSAGE, customMsgChannel)

    // 创建键盘事件的 data channel
    const keyboardChannel = new _SegDataChannel(
      this.pc.createDataChannel(DATA_CHANNEL_KEYBOARD, {
        ordered: true,
      })
    )
    this.dataChannels.set(DATA_CHANNEL_KEYBOARD, keyboardChannel)

    // 创建鼠标事件的 data channel
    const mouseChannel = new _SegDataChannel(
      this.pc.createDataChannel(DATA_CHANNEL_MOUSE, {
        ordered: true,
        maxPacketLifeTime: 60,
      })
    )
    this.dataChannels.set(DATA_CHANNEL_MOUSE, mouseChannel)

    // 创建处理鼠标 cursor 的 data channel
    const mouseCursorChannel = new _SegDataChannel(
      this.pc.createDataChannel(DATA_CHANNEL_MOUSE_CURSOR, {
        ordered: true,
      })
    )
    this.dataChannels.set(DATA_CHANNEL_MOUSE_CURSOR, mouseCursorChannel)

    mouseCursorChannel.setOnMessageListener((code, data) => {
      this.cursorProcessor.process(code, data)
    })
  }

  private async recoverDataChannels(
    peerConnection: RTCPeerConnection
  ): Promise<void> {
    if (this.pc !== peerConnection || this.isCleared) {
      return
    }

    // Agent 重启后，浏览器可能仍把旧 SCTP 通道标记为 open，但其远端关联
    // 已失效。软重连时强制重建所有客户端创建的通道，确保 Call、摄像头状态
    // 推送以及媒体恢复流程都能在新链路上重新建立。
    // console.warn("WebRTC force recreate data channels after ICE recovery")
    this.dataChannels.forEach((channel) => channel.close())
    this.dataChannels.clear()
    this.createDataChannels()

    const waitForAnswer = this.waitForMediaRenegotiation()
    await this.sendOffer(peerConnection)
    await waitForAnswer
  }

  private onGetDeviceInfo(data?: Uint8Array) {
    if (!data) {
      console.error("WebRTC receive get device info ack, but data is null")
      return
    }

    try {
      const msgData = data.subarray(Uint32Array.BYTES_PER_ELEMENT)
      const rawDeviceInfo = geelevel.protobuf.DeviceInfo.decode(msgData)
      const deviceInfo = JSON.parse(rawDeviceInfo.data as string) as DeviceInfo

      if (deviceInfo.display.length === 0) {
        // 上报远端设备未获取到桌面
        this.reportWebRTCLog("signal_agent_display_err")
        this.callOnConnect(CONNECT_STATE_PEER_NODISPLAY)
      }

      // 主显示器
      const primaryDisplay = deviceInfo.display.find((d) => d.is_primary)
      if (primaryDisplay) {
        console.log(
          "deviceInfo primaryDisplay: ",
          primaryDisplay.width,
          primaryDisplay.height
        )
      }

      // 获取主显示器分辨率
      let resolutions: Resolution[] = []
      if (primaryDisplay) {
        resolutions = primaryDisplay.settings
        if (primaryDisplay.width && primaryDisplay.height) {
          this.callbacks.onGetAgentResolution({
            width: primaryDisplay.width,
            height: primaryDisplay.height,
          })
        }
      }
      this.callbacks.onGetDisplayInfo(resolutions)
      this.agentBuildNum = deviceInfo.system.buildNumber
      // console.log(
      //   "test*** deviceInfo.system.buildNumber:",
      //   deviceInfo.system.buildNumber
      // )

      this.callbacks?.onGetAgentVersion(deviceInfo.system.buildNumber)
      this.callbacks.onGetKeyboardVersion(deviceInfo.keyboard_version)
      this.callbacks.onGetIsGameCloudComputer(
        deviceInfo.using_hardware_type === "vgpu"
        // deviceInfo.using_hardware_type === "quadro"
      )
      console.log(
        "using_hardware_type: ",
        deviceInfo.using_hardware_type,
        "，keyboard_version:",
        deviceInfo.keyboard_version,
        "，agentBuildNum:",
        deviceInfo.system.buildNumber
      )
    } catch (e) {
      console.error("WebRTC get device info ack decode error: ", e)
    }
  }

  // 控制文件传输
  // @param taskId: 传输任务id
  fileControl(taskId: number, action: number) {
    const msg = geelevel.protobuf.FileTransferControl.create({
      id: taskId,
      action,
    })
    const buffer = geelevel.protobuf.FileTransferControl.encode(msg).finish()
    this.sendCustomMessage(_MSG_CODE_FILE_TRANSFER_CONTROL, buffer)
  }

  // 获取到远端设备粘贴板数据
  private onGetClipboardData(data?: Uint8Array): void {
    if (!data) {
      console.error("WebRTC received clipboard data is null")
      return
    }
    try {
      const setClipboardData = geelevel.protobuf.SetClipboardData.decode(data)
      const type: string = setClipboardData.type
      const clipboardData: Uint8Array = setClipboardData.data
      if (this.onRemoteCopy !== null) {
        this.onRemoteCopy({ type, data: clipboardData })
      }
    } catch (e) {
      console.error("WebRTC clipboard data decode error: ", e)
    }
  }

  // 用户主动调用关闭连接
  disconnect(): void {
    if (this.pc.connectionState === "closed") {
      // 连接已关闭
      return
    }

    this.cancelPendingReconnect()
    this.endCall(_WebRTCCloseAction.USER_CLOSE, 0)
    this.clear()
    this.connStatistic.finish()
  }

  sendCall(): void {
    console.log("WebRTC send call")
    try {
      const callMsg = geelevel.protobuf.Call.create({
        password: this.peerConfig.token,
        pwdType: -1,
      })
      const buffer = geelevel.protobuf.Call.encode(callMsg).finish()
      this.sendCustomMessage(_MSG_CODE_CALL, buffer)
    } catch (e) {
      console.log("WebRTC send call error: ", e)
    }
  }

  private endCall(action: _WebRTCCloseAction, reason: number): void {
    try {
      const endCallMsg = geelevel.protobuf.EndCall.create({
        action,
        reason,
      })
      const buffer = geelevel.protobuf.EndCall.encode(endCallMsg).finish()
      this.sendCustomMessage(_MSG_CODE_END_CALL, buffer)
    } catch (e) {
      console.log("WebRTC send end call error: ", e)
    }
  }

  // 告知远端准备接收鼠标样式数据
  private setShowRemoteCursor() {
    assert(
      this.isConnected,
      "Cannot set show remote cursor, WebRTC not connected"
    )
    const showCursor = geelevel.protobuf.SetShowRemoteCursor.create({
      value: true,
      index: 0,
    })
    const buffer =
      geelevel.protobuf.SetShowRemoteCursor.encode(showCursor).finish()
    this.sendCustomMessage(_MSG_CODE_SHOW_REMOTE_CURSOR, buffer)
  }

  setOnCursorChange(callback: OnCursorChange) {
    this.cursorProcessor.setOnCursorChange(callback)
  }

  private getDeviceInfo() {
    const data = JSON.stringify({
      audio: [],
      display: [
        {
          name: "HDMI-1",
          label: "",
          multiple: 100,
          os: 2,
          x: 0,
          y: 0,
          width: 1920,
          height: 1080,
          is_primary: true,
          is_online: true,
        },
      ],
      id: _getLocalDevId(),
      system: {
        platform: "web",
      },
      type: "device-info",
    })
    const deviceInfo = geelevel.protobuf.DeviceInfo.create({ data })
    const deviceInfoBuffer =
      geelevel.protobuf.DeviceInfo.encode(deviceInfo).finish()
    const buffer = new Uint8Array(
      Uint32Array.BYTES_PER_ELEMENT + deviceInfoBuffer.length
    )
    const bufferView = new DataView(buffer.buffer)
    bufferView.setUint32(0, deviceInfoBuffer.length, true)
    buffer.set(deviceInfoBuffer, Uint32Array.BYTES_PER_ELEMENT)
    this.sendCustomMessage(_MSG_CODE_GET_PEER_DEVICE_INFO, buffer)
  }

  // 向远端设备粘贴板写入数据
  setClipboardData(type: string, data: ArrayBuffer): void {
    try {
      const setClipboardData = geelevel.protobuf.SetClipboardData.create({
        type,
        data: new Uint8Array(data),
      })
      const buffer =
        geelevel.protobuf.SetClipboardData.encode(setClipboardData).finish()
      this.sendCustomMessage(_MSG_CODE_SET_CUSTOM_DATA, buffer)
    } catch (e) {
      console.error("WebRTC set clipboard data encode error: ", e)
    }
  }

  async setWallpaper(url: string): Promise<number> {
    try {
      const setWallpaper: { picUrl: string } = {
        picUrl: url,
      }
      const buffer: Uint8Array =
        geelevel.protobuf.SetWallpaper.encode(setWallpaper).finish()
      const reply = await this.sendCustomMessageWithReply(
        _MSG_CODE_SET_WALLPAPER,
        buffer
      )
      if (reply === null || typeof reply.result !== "number") {
        return -1
      }
      return reply.result
    } catch (e) {
      console.error("WebRTC set wallpaper error: ", e)
      return -1
    }
  }

  async setScaleRatio(scaleRatio: number, index = 0): Promise<boolean> {
    try {
      const request = geelevel.protobuf.SetDpi.create({
        index,
        dpi: scaleRatio,
      })
      const buffer = geelevel.protobuf.SetDpi.encode(request).finish()
      const reply = await this.sendCustomMessageWithReply(
        _MSG_CODE_SET_VIDEO_DPI,
        buffer
      )
      return reply?.result === 0
    } catch (error) {
      console.error("WebRTC set scale ratio error: ", error)
      return false
    }
  }

  async getScaleRatioConfig(index = 0) {
    try {
      const request = geelevel.protobuf.GetDpi.create({ index })
      const buffer = geelevel.protobuf.GetDpi.encode(request).finish()
      const reply = await this.sendCustomMessageWithReply(
        _MSG_CODE_GET_VIDEO_DPI,
        buffer
      )
      if (reply?.result !== 0 || reply.data === undefined) return null
      return geelevel.protobuf.DpiInfo.decode(reply.data)
    } catch (error) {
      console.error("WebRTC get scale ratio config error: ", error)
      return null
    }
  }

  private async sendCustomMessageWithReply(
    code: number,
    data?: Uint8Array,
    timeoutMs = 5000
  ): Promise<CustomMessageReply | null> {
    return await new Promise((resolve) => {
      const waiters = this.replyWaiters.get(code) ?? []
      const onReply = (reply: CustomMessageReply | null) => {
        resolve(reply)
      }
      waiters.push(onReply)
      this.replyWaiters.set(code, waiters)

      const timer = window.setTimeout(() => {
        const currentWaiters = this.replyWaiters.get(code) ?? []
        this.replyWaiters.set(
          code,
          currentWaiters.filter((item) => item !== onReply)
        )
        if ((this.replyWaiters.get(code) ?? []).length === 0) {
          this.replyWaiters.delete(code)
        }
        resolve(null)
      }, timeoutMs)

      const wrappedReply = (reply: CustomMessageReply | null) => {
        window.clearTimeout(timer)
        onReply(reply)
      }

      const replacedWaiters = this.replyWaiters.get(code) ?? []
      replacedWaiters[replacedWaiters.length - 1] = wrappedReply
      this.replyWaiters.set(code, replacedWaiters)

      this.sendCustomMessage(code, data).catch((e) => {
        window.clearTimeout(timer)
        const currentWaiters = this.replyWaiters.get(code) ?? []
        this.replyWaiters.set(
          code,
          currentWaiters.filter((item) => item !== wrappedReply)
        )
        if ((this.replyWaiters.get(code) ?? []).length === 0) {
          this.replyWaiters.delete(code)
        }
        console.error("WebRTC send custom message with reply error: ", e)
        resolve(null)
      })
    })
  }

  setOnRemoteCopy(onCopy: OnRemoteCopy) {
    this.onRemoteCopy = onCopy
  }

  // 上报日志
  private reportWebRTCLog(event: string, data?: any) {
    _reportSDKLog(event, {
      ...(data ?? {}),
      client_ip: this.relayPubIp,
      agent_ip: this.relayInnerIp,
    })
  }

  private reportWebRTCError(errCode: number, data?: any) {
    _reportSDKError(errCode, {
      ...(data ?? {}),
      client_ip: this.relayPubIp,
      agent_ip: this.relayInnerIp,
    })
  }

  // 上报 ICE 网络连接信息
  private async reportICEStats() {
    const report = await this.pc.getStats()

    let transport: RTCTransportStats | undefined
    const candidatePairs = new Map<string, RTCIceCandidatePairStats>()
    const localCandidates = new Map<string, ICECandidateStats>()
    const remoteCandidates = new Map<string, ICECandidateStats>()
    report.forEach((r) => {
      switch (r.type) {
        case "candidate-pair":
          candidatePairs.set(r.id as string, r as RTCIceCandidatePairStats)
          break
        case "local-candidate":
          localCandidates.set(r.id as string, r as ICECandidateStats)
          break
        case "remote-candidate":
          remoteCandidates.set(r.id as string, r as ICECandidateStats)
          break
        case "transport":
          transport = r
          break
      }
    })
    if (transport?.selectedCandidatePairId === undefined) {
      return
    }
    const selectedPair = candidatePairs.get(transport.selectedCandidatePairId)
    if (!selectedPair) {
      return
    }

    const localCandidate = localCandidates.get(selectedPair.localCandidateId)
    const remoteCandidate = remoteCandidates.get(selectedPair.remoteCandidateId)
    if (!localCandidate || !remoteCandidate) {
      return
    }

    this.relayPubIp = this.peerConfig.forceRelay ? localCandidate.ip : null
    this.reportWebRTCLog("signal_link_info", {
      ip: localCandidate.ip, // 使用的ice ip信息
      network_type: localCandidate.networkType, // 客户端网络类型
      protocal: localCandidate.protocol, // 默认udp
      candidate_type: localCandidate.candidateType, // 默认使用网关中继转发
    })
    console.log("network_type sdk:", localCandidate.networkType)
  }

  // 开启音频输入
  async enableAudioInput(stream: MediaStream) {
    this.isAudioInputEnabled = true
    if (this.audioInputStream !== null && this.audioInputSender !== null) {
      // 音频输入流已开启
      return
    }
    this.audioInputStream = stream
    await this.enqueueMediaOperation(() => this.bindAudioInput())
  }

  private async recoverAudioInput(): Promise<void> {
    if (!this.isAudioInputEnabled || this.audioInputStream === null) {
      return
    }

    const track = this.audioInputStream.getAudioTracks()[0]
    if (track === undefined || track.readyState === "ended") {
      console.warn("WebRTC can not recover an ended audio input track")
      this.audioInputStream = null
      return
    }

    await this.enqueueMediaOperation(() => this.bindAudioInput())
  }

  private async bindAudioInput(): Promise<void> {
    const stream = this.audioInputStream
    const track = stream?.getAudioTracks()[0]
    if (stream === null || track === undefined) {
      throw new Error("WebRTC audio input stream has no audio track")
    }
    if (this.audioInputSender !== null) {
      return
    }

    this.mediaConfig = {
      ...this.mediaConfig,
      audioType: 2,
      recordDeviceIndex: -3,
      playerDeviceIndex: 0,
      echoCancellation: false,
    }
    const peerConnection = this.pc
    const sender = peerConnection.addTrack(track, stream)
    this.audioInputSender = sender
    this.isMediaRenegotiation = true
    const waitForAnswer = this.waitForMediaRenegotiation()
    try {
      await this.sendOffer()
      await waitForAnswer
    } catch (error) {
      if (this.audioInputSender === sender) {
        if (
          this.pc === peerConnection &&
          peerConnection.signalingState !== "closed"
        ) {
          peerConnection.removeTrack(sender)
        }
        this.audioInputSender = null
      }
      this.isMediaRenegotiation = false
      throw error
    }
  }

  // 关闭音频输入
  async disableAudioInput() {
    await this.enqueueMediaOperation(() => this.disableAudioInputInternal())
  }

  private async disableAudioInputInternal() {
    this.isAudioInputEnabled = false
    if (this.audioInputStream === null) {
      // 音频输入流已关闭
      return
    }

    this.mediaConfig = {
      ...this.mediaConfig,
      audioType: 1,
      recordDeviceIndex: -3,
      playerDeviceIndex: 0,
      echoCancellation: false,
    }
    // 关闭音频输入流
    const track = this.audioInputStream.getAudioTracks()[0]
    const sender =
      this.audioInputSender ??
      this.pc.getSenders().find((sender) => sender.track?.id === track.id)
    if (sender) {
      this.pc.removeTrack(sender)
    }
    this.audioInputSender = null
    track.stop()
    this.audioInputStream = null
    this.isMediaRenegotiation = true
    const waitForAnswer = this.waitForMediaRenegotiation()
    await this.sendOffer()
    await waitForAnswer
  }

  // 开启视频输入
  async enableVideoInput(stream: MediaStream) {
    const videoTrack = stream.getVideoTracks()[0]
    if (videoTrack === undefined) {
      throw new Error("WebRTC video input stream has no video track")
    }

    this.isVideoInputEnabled = true
    if (this.videoInputSender !== null) {
      // 关闭后复用已协商的 m-line，避免每次打开摄像头都等待 SDP 往返。
      // 本地流在关闭时仍会被停止，因此不会持续占用摄像头。
      try {
        await this.videoInputSender.replaceTrack(videoTrack)
        this.videoInputStream = stream
        return
      } catch (error) {
        this.isVideoInputEnabled = false
        throw error
      }
    }

    this.videoInputStream = stream
    await this.enqueueMediaOperation(() => this.bindVideoInput())
  }

  private async recoverVideoInput(): Promise<void> {
    // console.info("WebRTC recover video input after reconnect", {
    //   enabled: this.isVideoInputEnabled,
    //   hasStream: this.videoInputStream !== null,
    //   senderPresent: this.videoInputSender !== null,
    //   trackState:
    //     this.videoInputStream?.getVideoTracks()[0]?.readyState ?? "none",
    // })
    if (!this.isVideoInputEnabled || this.videoInputStream === null) {
      return
    }

    const track = this.videoInputStream.getVideoTracks()[0]
    if (track === undefined || track.readyState === "ended") {
      console.warn("WebRTC can not recover an ended video input track")
      this.videoInputStream = null
      return
    }

    await this.enqueueMediaOperation(() => this.bindVideoInput())
  }

  private async bindVideoInput(): Promise<void> {
    const stream = this.videoInputStream
    const videoTrack = stream?.getVideoTracks()[0]
    if (stream === null || videoTrack === undefined) {
      throw new Error("WebRTC video input stream has no video track")
    }
    if (this.videoInputSender !== null) {
      return
    }

    const hint = "detail"
    if ("contentHint" in videoTrack) {
      videoTrack.contentHint = hint
      if (videoTrack.contentHint !== hint) {
        console.error(`Invalid video track contentHint: "${hint}"`)
      }
    }

    const peerConnection = this.pc
    // console.info("WebRTC bind preserved video input to PeerConnection", {
    //   trackId: videoTrack.id,
    //   trackState: videoTrack.readyState,
    //   connectionState: peerConnection.connectionState,
    //   signalingState: peerConnection.signalingState,
    // })
    const transceiver = peerConnection.addTransceiver(videoTrack, {
      direction: "sendonly",
      streams: [stream],
    })
    const sender = transceiver.sender

    // 设置视频编码为 H264
    const codecs = RTCRtpSender.getCapabilities("video")?.codecs
    const preferCodec = "video/H264"
    if (codecs) {
      const preferredCodec = codecs.find((codec) => {
        return codec.mimeType === preferCodec
      })
      if (preferredCodec) {
        // 按顺序将 preferredCodec 放在列表最前面，其余编码器跟在后面
        const orderedCodecs = [
          preferredCodec,
          ...codecs.filter((codec) => codec.mimeType !== preferCodec),
        ]

        // 应用编码器偏好设置
        transceiver.setCodecPreferences(orderedCodecs)
      } else {
        console.warn("Can not find preferred video codec: ", preferCodec)
      }
    }

    this.videoInputSender = sender
    this.isMediaRenegotiation = true
    const waitForAnswer = this.waitForMediaRenegotiation()
    try {
      await this.sendOffer()
      await waitForAnswer
    } catch (error) {
      // 协商失败时恢复 SDK 状态；否则后续打开摄像头会被误判为已开启。
      if (this.videoInputSender === sender) {
        if (
          this.pc === peerConnection &&
          peerConnection.signalingState !== "closed"
        ) {
          peerConnection.removeTrack(sender)
        }
        this.videoInputSender = null
      }
      this.isMediaRenegotiation = false
      throw error
    }
  }

  // 关闭视频输入
  async disableVideoInput() {
    await this.enqueueMediaOperation(() => this.disableVideoInputInternal())
  }

  private async disableVideoInputInternal() {
    this.isVideoInputEnabled = false
    if (this.videoInputStream === null) {
      // 视频输入流已关闭
      return
    }

    const track = this.videoInputStream.getVideoTracks()[0]
    const sender =
      this.videoInputSender ??
      this.pc.getSenders().find((item) => item.track?.id === track?.id)
    if (sender) {
      // 保留 sender/m-line，下一次打开可直接 replaceTrack，不再触发 SDP 协商。
      await sender.replaceTrack(null)
      this.videoInputSender = sender
    }
    this.videoInputStream = null
  }

  // 上报渲染第一帧
  reportFirstFrameRender() {
    this.firstFrameRenderedReported = true
    if (this.firstFrameTimer !== null) {
      window.clearTimeout(this.firstFrameTimer)
      this.firstFrameTimer = null
    }
    this.reportWebRTCLog("signal_drm_render_start")
    try {
      const jsonData = geelevel.protobuf.JsonData.create({
        // Agent 按秒解析该时间字段，因此不要传入毫秒时间戳。
        data: JSON.stringify({
          msg_id: _CUSTOM_MSG_ID_FIRST_FRAME_RENDER,
          time: Math.floor(Date.now() / 1000),
        }),
      })
      const buffer = geelevel.protobuf.JsonData.encode(jsonData).finish()
      void this.sendCustomMessage(_MSG_CODE_SEND_JSON_DATA, buffer).catch(
        (error) => {
          console.warn("WebRTC first frame event was not sent", error)
        }
      )
    } catch (error) {
      console.error("WebRTC report first frame render error: ", error)
    }
  }

  // 获取音视频流
  getMediaStreams(): MediaStreams {
    return this.mediaStreams
  }

  // 添加连接状态持续监控
  private connectionMonitorTimer: number | null = null

  private startConnectionMonitoring() {
    // 清除之前的监控
    if (this.connectionMonitorTimer) {
      clearInterval(this.connectionMonitorTimer)
    }

    // 每5秒检查一次连接状态
    this.connectionMonitorTimer = window.setInterval(() => {
      if (!this.isCleared && this.pc) {
        const isSafari =
          navigator.userAgent.includes("Safari") &&
          !navigator.userAgent.includes("Chrome")

        const monitorInfo = {
          connectionState: this.pc.connectionState,
          iceConnectionState: this.pc.iceConnectionState,
          signalingState: this.pc.signalingState,
          iceGatheringState: this.pc.iceGatheringState,
          isSafari,
          timestamp: Date.now(),
        }

        // console.log("wlp Connection monitor check:", monitorInfo)

        // Safari 特定的连接问题检测
        if (isSafari) {
          // ICE 收集超时检测
          if (
            this.pc.iceGatheringState === "gathering" &&
            Date.now() - this.startConnTime! > 30000
          ) {
            // console.error("wlp Safari ICE gathering timeout")
            this.reportWebRTCError(ERR_ICE, {
              msg: "Safari ICE收集超时",
              state: monitorInfo,
            })
          }

          // 连接建立超时检测
          if (
            this.pc.iceConnectionState === "checking" &&
            Date.now() - this.startConnTime! > 45000
          ) {
            // console.error("wlp Safari ICE connection timeout")
            this.reportWebRTCError(ERR_ICE, {
              msg: "Safari ICE连接超时",
              state: monitorInfo,
            })
          }

          // 检测Safari中的异常状态
          if (this.pc.connectionState === "closed" && !this.isCleared) {
            // console.warn(
            //   "wlp Safari connection unexpectedly closed, attempting recovery"
            // )
            this.reportWebRTCError(ERR_ICE, {
              msg: "Safari连接意外关闭",
              state: monitorInfo,
            })
            this.tryReconnect()
          }

          // TURN 服务器连接状态检测
          this.checkTurnServerConnection()
        }
      }
    }, 5000)
  }

  // 检测 TURN 服务器连接状态
  private async checkTurnServerConnection() {
    try {
      const stats = await this.pc.getStats()
      let hasRelayCandidates = false
      let hasHostCandidates = false
      let hasSrflxCandidates = false
      const turnServerErrors: any[] = []
      const candidateStats = {
        relay: 0,
        host: 0,
        srflx: 0,
        prflx: 0,
      }

      stats.forEach((report) => {
        if (report.type === "local-candidate") {
          const candidateType = report.candidateType
          switch (candidateType) {
            case "relay":
              hasRelayCandidates = true
              candidateStats.relay++
              break
            case "host":
              hasHostCandidates = true
              candidateStats.host++
              break
            case "srflx":
              hasSrflxCandidates = true
              candidateStats.srflx++
              break
            case "prflx":
              candidateStats.prflx++
              break
          }
        }
        if (report.type === "candidate-pair" && report.state === "failed") {
          turnServerErrors.push(report)
        }
      })

      const isSafari =
        navigator.userAgent.includes("Safari") &&
        !navigator.userAgent.includes("Chrome")

      if (isSafari) {
        if (!hasRelayCandidates && this.peerConfig.forceRelay) {
          // console.error(
          //   "wlp Safari: No relay candidates found but forceRelay is true"
          // )

          this.reportWebRTCError(ERR_ICE, {
            msg: "Safari未找到TURN服务器候选者",
            forceRelay: this.peerConfig.forceRelay,
            iceServers: this.rtcConfig.iceServers,
            candidateStats,
            diagnostics: "TURN服务器可能不支持Safari或认证失败",
          })

          // 如果没有任何候选者，可能需要重新尝试连接
          if (
            candidateStats.relay === 0 &&
            candidateStats.host === 0 &&
            candidateStats.srflx === 0
          ) {
            // 触发重连或错误处理
            setTimeout(() => {
              if (!this.isConnected && !this.isCleared) {
                this.tryReconnect()
              }
            }, 3000)
          }
        }

        // 如果有其他类型的候选者但没有 relay，给出建议
        if (!hasRelayCandidates && (hasHostCandidates || hasSrflxCandidates)) {
          this.reportWebRTCError(ERR_ICE, {
            msg: "Safari仅找到非中继候选者",
            candidateStats,
            suggestion: "建议检查TURN服务器配置或网络环境",
          })
        }
      }

      if (turnServerErrors.length > 0) {
        // console.error("wlp TURN server connection errors:", turnServerErrors)
        this.reportWebRTCError(ERR_ICE, {
          msg: "TURN服务器连接错误",
          errors: turnServerErrors.length,
          isSafari,
          candidateStats,
        })
      }
    } catch (error) {
      this.reportWebRTCError(ERR_ICE, {
        msg: "检查TURN服务器状态时发生错误",
        error: error instanceof Error ? error.message : String(error),
        isSafari:
          navigator.userAgent.includes("Safari") &&
          !navigator.userAgent.includes("Chrome"),
      })
    }
  }
}
